import { computed, ref } from 'vue';

interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NavigatorWithStandalone extends Navigator {
  readonly standalone?: boolean;
}

function isInstallPrompt(event: Event): event is InstallPrompt {
  return 'prompt' in event && typeof event.prompt === 'function' && 'userChoice' in event;
}

const WEEK = 7 * 24 * 60 * 60 * 1000;
const installed = ref(false);
const dismissedUntil = ref(0);
const prompt = ref<InstallPrompt | null>(null);
const helpOpen = ref(false);
const busy = ref(false);
const now = ref(Date.now());
let storageKey = '';
let initialized = false;
const instructions = ref('Abra o menu do navegador e escolha “Instalar aplicativo” ou “Adicionar à tela inicial”. No Chrome e no Edge para computador, você também pode usar o ícone de instalação na barra de endereço.');

function isAppleMobile(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

function isDesktopSafari(): boolean {
  return /safari/i.test(navigator.userAgent)
    && !/(chrome|crios|chromium|edg|opr|android)/i.test(navigator.userAgent)
    && !isAppleMobile();
}

function persist(value: number): void {
  dismissedUntil.value = value;
  try { localStorage.setItem(storageKey, String(value)); } catch { /* Preferência opcional: manter em memória. */ }
}

export function initializeAppInstall(options: { storageKey: string; legacyStorageKey?: string }): void {
  if (initialized) return;
  initialized = true;
  storageKey = options.storageKey;
  try {
    const stored = localStorage.getItem(storageKey);
    const legacy = options.legacyStorageKey ? localStorage.getItem(options.legacyStorageKey) : null;
    if (stored === 'off' || (stored === null && legacy === 'off')) persist(Date.now() + WEEK);
    else {
      const parsed = Number(stored);
      dismissedUntil.value = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }
  } catch { /* Instalação permanece disponível sem armazenamento. */ }
  const media = window.matchMedia('(display-mode: standalone)');
  const updateMode = (): void => {
    now.value = Date.now();
    const browserNavigator: NavigatorWithStandalone = navigator;
    installed.value = media.matches || browserNavigator.standalone === true;
  };
  updateMode();
  media.addEventListener('change', updateMode);
  window.addEventListener('focus', updateMode);
  document.addEventListener('visibilitychange', updateMode);
  if (isAppleMobile()) {
    instructions.value = 'No iPhone ou iPad, abra este endereço no Safari, toque em Compartilhar e escolha “Adicionar à Tela de Início”.';
  } else if (isDesktopSafari()) {
    instructions.value = 'No Safari para Mac, abra o menu Arquivo e escolha “Adicionar ao Dock”.';
  }
  window.addEventListener('beforeinstallprompt', (event) => {
    if (!isInstallPrompt(event)) return;
    event.preventDefault();
    prompt.value = event;
  });
  window.addEventListener('appinstalled', () => { installed.value = true; prompt.value = null; helpOpen.value = false; });
}
export function useAppInstall() {
  const dismiss = (): void => { persist(Date.now() + WEEK); };
  const install = async (): Promise<void> => {
    if (installed.value || busy.value) return;
    const event = prompt.value;
    if (!event) { helpOpen.value = true; return; }
    prompt.value = null;
    busy.value = true;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice.outcome === 'dismissed') dismiss();
      else persist(Date.now() + WEEK); // Só `appinstalled` confirma a instalação.
    } catch { helpOpen.value = true; }
    finally { busy.value = false; }
  };
  return { installed, helpOpen, instructions, busy, install, dismiss,
    canInstall: computed(() => prompt.value !== null),
    showBanner: computed(() => !installed.value && dismissedUntil.value <= now.value),
    closeHelp: (): void => { helpOpen.value = false; },
  };
}
